import { ObjectType } from '@csp/csp-common-model';
import { DeviceModelConfigPropertiesV2 } from '../device-config-properties/DeviceModelConfigPropertiesV2';

export enum ObservationContext {
  CUSTOM = 'CUSTOM',
  DEFAULT = 'DEFAULT',
  COMPEX = 'COMPEX',
  SPIROMETRY = 'SPIROMETRY',
  ON_SITE_SPIROMETRY = 'ON_SITE_SPIROMETRY',
  IN_CLINIC_SPIROMETRY = 'IN_CLINIC_SPIROMETRY',
  PULSE_OX_WALKING = 'PULSE_OX_WALKING',
  PULSE_OX_AT_REST = 'PULSE_OX_AT_REST',
  PULSE_OX_AT_REST_ON_DEMAND = 'PULSE_OX_AT_REST_ON_DEMAND',
}

/**
 * It will match any context name that starts with CUSTOM_ or it's just CUSTOM
 *
 * Example: CUSTOM, CUSTOM_WALKING, CUSTOM_AT_REST, CUSTOM_WHILE_TALKING
 */
export type CustomObservationContextType = 'CUSTOM' | `CUSTOM_${string}`;

export type ObservationContextType = keyof typeof ObservationContext | CustomObservationContextType;

/**
 * All context config related props available for picking
 */
export type ConfigProperties = Pick<
  DeviceModelConfigPropertiesV2,
  | 'lastSyncDateSource'
  | 'groupedObservations'
  | 'isObservationDetailsPageEnabled'
  | 'deviceObservationColumns'
  | 'deviceObservationUnits'
  | 'sessionType'
  | 'requestDetailsListType'
  | 'hideMeasurementInstructionsLink'
  | 'collectAmbientTemperature'
  | 'defaultAmbientTemperatureCelsius'
  | 'externalReportUrl'
  | 'allowRetakeMeasurement'
  | 'deviceObservationFlowConfig'
  | 'requireOnline'
>;

/**
 * Required config props that should be populated for every context type
 */
export type ContextConfigRequiredProperties = Required<
  Pick<
    ConfigProperties,
    | 'lastSyncDateSource'
    | 'groupedObservations'
    | 'isObservationDetailsPageEnabled'
    | 'deviceObservationColumns'
    | 'deviceObservationUnits'
    | 'requestDetailsListType'
    | 'hideMeasurementInstructionsLink'
    | 'allowRetakeMeasurement'
    | 'requireOnline'
  >
>;

/**
 * Optional config props that can be populated for every context type
 */
export type ContextConfigOptionalProperties = Pick<ConfigProperties, 'deviceObservationFlowConfig'>;

/**
 * Additional config props that can be picked per context type
 */
export type ContextConfigAdditionalProperties = Omit<ConfigProperties, keyof ContextConfigRequiredProperties>;

/**
 * Config props that can be overridden on request level
 */
export type ContextConfigOverridableProperties = ConfigProperties;

export type GenericDeviceObservationContext<
  Type extends ObservationContextType = ObservationContextType,
  ContextConfig extends ContextConfigAdditionalProperties = ContextConfigAdditionalProperties,
> = {
  type: Type;
  contextConfig: ContextConfigRequiredProperties & ContextConfig & ContextConfigOptionalProperties;
};

/*
 * Build DEFAULT context
 */
type DefaultContextConfigAdditionalProperties = ObjectType;
export type DefaultContextConfig = GenericDeviceObservationContext<'DEFAULT', DefaultContextConfigAdditionalProperties>;

/*
 * Build COMPEX Context
 */
type CompexContextConfigAdditionalProperties = ObjectType;
export type CompexContextConfig = GenericDeviceObservationContext<'COMPEX', CompexContextConfigAdditionalProperties>;

/*
 * Build SPIROMETRY context
 */
type SpirometryContextConfigAdditionalProperties = Required<
  Pick<
    ContextConfigAdditionalProperties,
    'sessionType' | 'collectAmbientTemperature' | 'defaultAmbientTemperatureCelsius'
  >
>;
export type SpirometryContextConfig = GenericDeviceObservationContext<
  'SPIROMETRY',
  SpirometryContextConfigAdditionalProperties
>;

/*
 * Build ON_SITE_SPIROMETRY Context
 */
type OnSiteSpirometryContextConfigAdditionalProperties = ObjectType;
export type OnSiteSpirometryContextConfig = GenericDeviceObservationContext<
  'ON_SITE_SPIROMETRY',
  OnSiteSpirometryContextConfigAdditionalProperties
>;

/*
 * Build IN_CLINIC_SPIROMETRY Context
 */
type InClinicSpirometryContextConfigAdditionalProperties = ObjectType;
export type InClinicSpirometryContextConfig = GenericDeviceObservationContext<
  'IN_CLINIC_SPIROMETRY',
  InClinicSpirometryContextConfigAdditionalProperties
>;

/*
 * Build PULSE_OX_WALKING Context
 */
type WalkingMasimoContextConfigAdditionalProperties = ObjectType;
export type WalkingMasimoContextConfig = GenericDeviceObservationContext<
  'PULSE_OX_WALKING',
  WalkingMasimoContextConfigAdditionalProperties
>;

/*
 * Build PULSE_OX_AT_REST Context
 */
type AtRestMasimoContextConfigAdditionalProperties = ObjectType;
export type AtRestMasimoContextConfig = GenericDeviceObservationContext<
  'PULSE_OX_AT_REST',
  AtRestMasimoContextConfigAdditionalProperties
>;

/*
 * Build PULSE_OX_AT_REST_ON_DEMAND Context
 */
type AtRestOnDemandMasimoContextConfigAdditionalProperties = ObjectType;
export type AtRestOnDemandMasimoContextConfig = GenericDeviceObservationContext<
  'PULSE_OX_AT_REST_ON_DEMAND',
  AtRestOnDemandMasimoContextConfigAdditionalProperties
>;

/*
 * Build CUSTOM Context
 */
type CustomContextConfigAdditionalProperties = ObjectType;
export type CustomContextConfig =
  | GenericDeviceObservationContext<'CUSTOM', CustomContextConfigAdditionalProperties>
  | GenericDeviceObservationContext<`CUSTOM_${string}`, CustomContextConfigAdditionalProperties>;

export type ObservationContextConfig =
  | CustomContextConfig
  | CompexContextConfig
  | DefaultContextConfig
  | SpirometryContextConfig
  | OnSiteSpirometryContextConfig
  | InClinicSpirometryContextConfig
  | WalkingMasimoContextConfig
  | AtRestMasimoContextConfig
  | AtRestOnDemandMasimoContextConfig;

export type ToObservationContextConfig<T extends ObservationContextConfig['type']> = Extract<
  ObservationContextConfig,
  { type: T }
>;

export type ObservationContextConfigMap = {
  [K in ObservationContextConfig['type']]: ToObservationContextConfig<K>;
};
